import { Client } from './client';
import { MessageStruct, MessagePayloadType, getMessageJsonPayload } from './message';
import { WebCodecsPlayer } from './webcodecs-player';
import { NestedTypedEventEmitter3 } from './event-emitter';
import { PlayerEvents } from './webcodecs-player';
import { SignalQualityMonitor } from './quality';

export { PlayerEvents } from './webcodecs-player';

export class Player extends NestedTypedEventEmitter3<PlayerEvents> {
  private _webcodecsPlayer: WebCodecsPlayer;
  private _session: Client;

  private _qualityMoniAudio: SignalQualityMonitor = new SignalQualityMonitor();
  private _qualityMoniVideo: SignalQualityMonitor = new SignalQualityMonitor();

  private _statsInterval = 0;

  public totalBytesReceived = 0;
  public bytesReceivedSinceLastInterval = 0;
  public lastStatsTime = performance.now();
  public currentBitrate = 0;
  public rtt = 0;
  public lateFramesCount = 0;
  public totalLatenessMs = 0;
  public lateFramesTotalCount = 0;

  public avSyncOffsetSumUs = 0;
  public avSyncSampleCount = 0;
  public avSyncMaxOffsetUs = 0;

  private lateFrameThresholdMs = 200;

  constructor() {
    super();

    this._webcodecsPlayer = new WebCodecsPlayer();

    this._webcodecsPlayer.on('timeupdate', () => {
      this.emit('timeupdate');
    });

    this._webcodecsPlayer.on('error', (msg, err) => {
      console.error('Player error:', msg, err);
    });

    this._session = new Client();
  }

  async play(serverUrl: string, canvasEl: HTMLCanvasElement) {
    if (this._session.isConnected) {
      return;
    }

    const canvasCtx = canvasEl.getContext('2d');
    if (!canvasCtx) {
      throw new Error('Could not get canvas drawing context');
    }

    this._webcodecsPlayer.setCanvasDrawImage(canvasCtx, canvasEl.width, canvasEl.height);

    if (this._session.isDisposed) {
      this._session = new Client();
    }

    this._session.on('ready', () => {
      console.info('Client ready');
    });

    this._session.on('error', (err) => {
      console.error('Client error event:', err);
    });

    this._session.on('message', (msg: MessageStruct, groupId, tag, streamIdx) => {
      this.onSessionMessage(this._webcodecsPlayer, msg, groupId, tag, streamIdx);
    });

    this._session.on('log', (msg: string) => {
      // do we need to log here?
    });

    this._session.on('closed', () => {
      console.info('Client closed');
    });

    this._statsInterval = window.setInterval(() => {
      const currentTime = performance.now();
      const timeElapsed = (currentTime - this.lastStatsTime) / 1000; // in seconds
      this.currentBitrate = (this.bytesReceivedSinceLastInterval * 8) / timeElapsed; // bits per second

      this.bytesReceivedSinceLastInterval = 0;
      this.lastStatsTime = currentTime;

      this.updateAVSyncMetrics();

      this.emit('stats', false);

      this.lateFramesCount = 0;
      this.totalLatenessMs = 0;
      this.lateFramesTotalCount = 0;

      this.avSyncOffsetSumUs = 0;
      this.avSyncSampleCount = 0;
      this.avSyncMaxOffsetUs = 0;
    }, 1000);

    this.emit('stats', true);

    try {
      await this._session.connect(serverUrl);
    } catch (error) {
      console.error('Failed to connect session:', error);
    }
  }

  pause() {
    this._session.dispose();
    window.clearInterval(this._statsInterval);
  }

  setVolume(volume: number) {
    volume = Math.max(Math.min(100, volume), 0);
    const gain = volume / 100;
    this._webcodecsPlayer.setVolume(gain);
  }

  onSessionMessage(
    player: WebCodecsPlayer,
    msg: MessageStruct,
    groupId: number,
    tag: string,
    streamIdx: number
  ) {
    const timeUs = Number(msg.time);

    const payloadSize = msg.payload.byteLength;
    this.totalBytesReceived += payloadSize;
    this.bytesReceivedSinceLastInterval += payloadSize;

    switch (msg.flagsParsed.payloadType) {
      case MessagePayloadType.Json: {
        const payload = getMessageJsonPayload(msg);
        // console.log('JSON message:', { groupId, tag, streamIdx }, payload);
        player.handleJsonMessage(payload);

        break;
      }
      case MessagePayloadType.Avc: {
        const currentTimeUs = performance.now() * 1000; 
        const latenessMs = (currentTimeUs - timeUs) / 1000;

        if (latenessMs > this.lateFrameThresholdMs) {
          this.lateFramesCount += 1;
          this.lateFramesTotalCount += 1;
          this.totalLatenessMs += latenessMs - this.lateFrameThresholdMs;

          this._qualityMoniVideo.onFrameDropped();
          return;
        }

        this._qualityMoniVideo.onFrame(timeUs / 1000, payloadSize);
        player.appendVideoBuffer({
          payloadUnit: msg.payload,
          dtsUs: timeUs,
          ptoUs: msg.pto,
          isKeyframe: msg.flagsParsed.isKeyframe,
          isCodecConfig: msg.flagsParsed.isCodecConfig,
        });
        break;
      }
      case MessagePayloadType.Aac: {
        this._qualityMoniAudio.onFrame(timeUs / 1000, payloadSize);
        player.appendAudioBuffer({
          payloadUnit: msg.payload,
          dtsUs: timeUs,
          ptoUs: msg.pto,
          isKeyframe: msg.flagsParsed.isKeyframe,
          isCodecConfig: msg.flagsParsed.isCodecConfig,
        });
        break;
      }
    }
  }

  getAverageLatenessMs(): number {
    if (this.lateFramesTotalCount === 0) return 0;
    return this.totalLatenessMs / this.lateFramesTotalCount;
  }

  getAverageAVSyncOffsetUs(): number {
    if (this.avSyncSampleCount === 0) return 0;
    return this.avSyncOffsetSumUs / this.avSyncSampleCount;
  }

  updateAVSyncMetrics() {
    const offsetUs = this.getAVSyncOffsetUs();
    this.avSyncOffsetSumUs += offsetUs;
    this.avSyncSampleCount += 1;
    this.avSyncMaxOffsetUs = Math.max(this.avSyncMaxOffsetUs, Math.abs(offsetUs));
  }

  getAVSyncOffsetUs(): number {
    const audioTimeUs = this.audioCurrentTimeUs;
    const videoTimeUs = this.videoCurrentTimeUs;
    return audioTimeUs - videoTimeUs;
  }

  getPlayClockUs(): number {
    return this._webcodecsPlayer.getPlayClockUs();
  }

  get audioCurrentTimeUs(): number {
    return this._webcodecsPlayer.audioCurrentTimeUs;
  }

  get videoCurrentTimeUs(): number {
    return this._webcodecsPlayer.videoCurrentTimeUs;
  }

  getAudioScheduledLengthUs() {
    return this._webcodecsPlayer.getAudioScheduledLengthUs();
  }

  async getTransportConnectionStats(): Promise<string | WebTransportStats> {
    return this._session.getTransportConnectionStats();
  }

  getTransportStatusInfo() {
    return this._session.getTransportStatusInfo();
  }

  getTransportStats() {
    return this._session.getTransportStats();
  }

  getQualityMetrics() {
    const audioQuality = this._qualityMoniAudio.getMetrics();
    const videoQuality = this._qualityMoniVideo.getMetrics();
    return {
      audioQuality,
      videoQuality,
    };
  }

  getVideoCodecInfo() {
    return this._session.getVideoCodecInfo();
  }

  getAudioCodecInfo() {
    return this._session.getAudioCodecInfo();
  }
}